import React, { useCallback, useEffect, useState } from 'react';
import { Worlds, errorMessage } from '../../api/client';
import { LogEntry, World } from '../../types';
import { Icon } from '../ui/Icon';
import { Empty, Loading, Note } from '../ui/primitives';
import { logLevel } from '../../utils/format';
import { downloadLogs, fmtLogTs } from '../../utils/logs';

/** The backend returns at most this many lines per query (Loki `limit`). */
const PAGE_LIMIT = 1000;

type Preset = '1h' | '6h' | '24h' | '7d' | 'custom';
const PRESET_MS: Record<Exclude<Preset, 'custom'>, number> = {
  '1h': 60 * 60 * 1000,
  '6h': 6 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
};
const PRESET_LABEL: Record<Preset, string> = { '1h': '1 h', '6h': '6 h', '24h': '24 h', '7d': '7 d', custom: 'Custom' };

interface Range {
  from: number;
  to: number;
}

const merge = (a: LogEntry[], b: LogEntry[]): LogEntry[] => {
  const seen = new Set<string>();
  const out: LogEntry[] = [];
  [...a, ...b].forEach((l) => {
    const k = `${l.timestamp}|${l.message}`;
    if (!seen.has(k)) {
      seen.add(k);
      out.push(l);
    }
  });
  return out.sort((x, y) => y.timestamp - x.timestamp);
};

/** `YYYY-MM-DDTHH:mm` in local time, the value a datetime-local input wants. */
const toLocalInput = (ms: number): string => {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** A zoneless `YYYY-MM-DDTHH:mm` parses as local time. */
const fromLocalInput = (value: string): number | null => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const tzLabel = (): string => {
  const off = -new Date().getTimezoneOffset();
  const sign = off >= 0 ? '+' : '-';
  const abs = Math.abs(off);
  return `UTC${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
};

interface Props {
  world: World;
}

/** Older console output from Loki, for any time range. Newest first. */
const HistoryLogs: React.FC<Props> = ({ world }) => {
  const [preset, setPreset] = useState<Preset>('24h');
  const [fromInput, setFromInput] = useState(() => toLocalInput(Date.now() - PRESET_MS['24h']));
  const [toInput, setToInput] = useState(() => toLocalInput(Date.now()));
  const [range, setRange] = useState<Range | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exhausted, setExhausted] = useState(false);

  const load = useCallback(
    async (r: Range) => {
      setLoading(true);
      setError(null);
      try {
        const l = await Worlds.logs(world.id, r.from, r.to);
        setLogs(merge([], l));
        setExhausted(l.length < PAGE_LIMIT);
      } catch (e) {
        setError(errorMessage(e, 'Failed to load logs.'));
      } finally {
        setLoading(false);
      }
    },
    [world.id]
  );

  // Presets resolve against "now" each time they are picked; custom waits for Apply.
  useEffect(() => {
    if (preset === 'custom') return;
    const now = Date.now();
    const r = { from: now - PRESET_MS[preset], to: now };
    setRange(r);
    load(r);
  }, [preset, load]);

  const applyCustom = () => {
    const from = fromLocalInput(fromInput);
    const to = fromLocalInput(toInput);
    if (from == null || to == null) {
      setError('Enter both a start and an end time.');
      return;
    }
    if (from >= to) {
      setError('The start must be before the end.');
      return;
    }
    const r = { from, to };
    setRange(r);
    load(r);
  };

  const refresh = () => {
    if (preset === 'custom') {
      if (range) load(range);
    } else {
      const now = Date.now();
      const r = { from: now - PRESET_MS[preset], to: now };
      setRange(r);
      load(r);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !range || logs.length === 0) return;
    const oldest = Math.min(...logs.map((l) => l.timestamp));
    setLoadingMore(true);
    try {
      const l = await Worlds.logs(world.id, range.from, oldest);
      if (l.length) setLogs((prev) => merge(prev, l));
      if (l.length < PAGE_LIMIT) setExhausted(true);
    } catch (e) {
      setError(errorMessage(e, 'Failed to load earlier logs.'));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <>
      <div className="term-bar">
        <div className="seg" role="group" aria-label="Time range">
          {(Object.keys(PRESET_LABEL) as Preset[]).map((p) => (
            <button key={p} type="button" aria-pressed={preset === p} onClick={() => setPreset(p)}>
              {PRESET_LABEL[p]}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <>
            <input type="datetime-local" className="input input--mono" value={fromInput} max={toInput} onChange={(e) => setFromInput(e.target.value)} aria-label="From" />
            <span style={{ color: 'var(--ink3)', fontSize: 12 }}>to</span>
            <input type="datetime-local" className="input input--mono" value={toInput} min={fromInput} onChange={(e) => setToInput(e.target.value)} aria-label="To" />
            <button type="button" className="btn btn--sm btn--primary" onClick={applyCustom} disabled={loading}>
              Apply
            </button>
            <span style={{ color: 'var(--ink3)', fontSize: 11.5 }}>times are local ({tzLabel()})</span>
          </>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn btn--sm" onClick={refresh} disabled={loading}>
          <Icon name="refresh" size={12} /> Refresh
        </button>
        <button type="button" className="btn btn--sm" onClick={() => downloadLogs(world, logs)} disabled={logs.length === 0}>
          <Icon name="download" size={12} /> Export .txt
        </button>
      </div>
      {error && <Note tone="danger">{error}</Note>}
      {loading ? (
        <Loading label="Querying Loki…" />
      ) : logs.length === 0 ? (
        <Empty icon="log" title="No log lines in this range">
          {range ? `Nothing between ${fmtLogTs(range.from)} and ${fmtLogTs(range.to)}.` : 'Pick a time range above.'}
        </Empty>
      ) : (
        <div className="term">
          {logs.map((l, i) => {
            const lv = logLevel(l.message);
            return (
              <div className="term-row" key={`${l.timestamp}-${i}`}>
                <span className="t">{fmtLogTs(l.timestamp)}</span>
                <span className={`lv-${lv}`}>{lv === 'err' ? 'ERROR' : lv.toUpperCase()}</span>
                <span>{l.message}</span>
              </div>
            );
          })}
          <div className="term-more">
            <button type="button" className="btn btn--sm" onClick={loadMore} disabled={loadingMore || exhausted}>
              {loadingMore ? <span className="spin" /> : <Icon name="clock" size={12} />}
              {exhausted ? 'No earlier lines in this range' : loadingMore ? 'Loading…' : 'Load earlier in this range'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default HistoryLogs;
